import { DataItem } from "./panel-menu.types";
import { NavTreeState } from "./navContext.types";
import {
  NavTreeAction,
  NavTreeType,
  NavTreeActionType,
} from "./navReducer.types";
import { getBreakpoint, isSmallScreen } from "../utils";
import { findActiveItem, findParentsIds } from "./navReducer.utils";

export const reducer = (
  state: NavTreeState,
  action: NavTreeAction
): NavTreeState => {
  switch (action.type) {
    case NavTreeActionType.REPLACE_STATE: {
      const navTreeType = action.isHeaderSecondary
        ? NavTreeType.SECONDARY
        : NavTreeType.PRIMARY;

      // Find defaultActive item
      const defaultActiveItem = findActiveItem(action.changedTree, true);

      // Find active item when nav tree is being changed/replaced
      const newActiveItem = findActiveItem(action.changedTree);

      // Resolve new active item ID. Ignore `defaultActive` value if `isActive` prop is present or if any `activeItem` is already in the state
      const newActiveItemId =
        newActiveItem?.id || (!state.activeItem && defaultActiveItem?.id);

      if (newActiveItemId) {
        /**
         * Activates and expands parent items
         */

        const _activeItemParents =
          findParentsIds(action.changedTree, newActiveItemId) || [];

        // Unique expanded items
        const _expandedItems = [
          ...state.expandedItems,
          ..._activeItemParents,
        ].filter((item, i, ar) => ar.indexOf(item) === i);

        return {
          ...state,
          activeItem: newActiveItemId,
          activeItemParents: _activeItemParents,
          expandedItems: _expandedItems,
          [navTreeType]: action.changedTree,
        };
      }

      return {
        ...state,
        [navTreeType]: action.changedTree,
      };
    }

    case NavTreeActionType.ADD_ACTIVE: {
      const clickedNavTreeType = action.isHeaderSecondary
        ? NavTreeType.SECONDARY
        : NavTreeType.PRIMARY;

      const _activeItemParents =
        findParentsIds(state[clickedNavTreeType], action.itemId) || [];

      return {
        ...state,
        activeItem: action.itemId,
        activeItemParents: _activeItemParents,
      };
    }

    case NavTreeActionType.TOGGLE_EXPAND: {
      const _expandedItems = state.expandedItems.includes(action.itemId)
        ? state.expandedItems.filter((item) => item !== action.itemId)
        : [...state.expandedItems, action.itemId];

      return {
        ...state,
        expandedItems: _expandedItems,
      };
    }

    case NavTreeActionType.SET_HEADER_META_DATA: {
      const computedBreakpoint = getBreakpoint(
        action.headerWidth,
        action.customBreakpoints
      );

      return {
        ...state,
        headerMetaData: {
          breakpoint: computedBreakpoint,
          isSmallScreen: isSmallScreen(computedBreakpoint),
        },
      };
    }

    case NavTreeActionType.SET_ITEMS_WIDTH: {
      const measured = !!action.navItemsWidth.length;

      return {
        ...state,
        navItemsWidth: action.navItemsWidth,
        measureInProgress: !measured,
      };
    }

    case NavTreeActionType.CLEAR_AND_START_MEASUREMENT: {
      return {
        ...state,
        navItemsWidth: [],
        measureInProgress: true,
      };
    }

    case NavTreeActionType.SET_ITEMS_VISIBILITY: {
      let totalItemsWidth = action.totalItemsWidth;

      const newNavTree = state.navTree.reduce(
        (accumulator: DataItem[], item, index) => {
          totalItemsWidth += state.navItemsWidth[index] ?? 0;

          accumulator.push({
            ...item,
            _privateProps: {
              ...item._privateProps,
              // For small screen sizes we want all items to be visible
              // since they will appear in the hamburguer menu
              visible:
                state.headerMetaData?.isSmallScreen ||
                totalItemsWidth < action.menuContainerWidth,
            },
          });

          return accumulator;
        },
        []
      );

      return {
        ...state,
        navTree: newNavTree,
      };
    }

    default:
      return state;
  }
};
